import { Divider } from '@vrplatform/components';
import {
  useBilling,
  useDashboard,
  useEnabledFeatures,
  useIsOwnerPortalPreview,
  useOwnerPortalContact,
  useSettingsPermission,
  useTeam,
  useTeamMembership,
  useTeamPermissions,
  useTeamRole,
  useUiPermissionRoles,
} from '@vrplatform/data';
import { HiddenFeatureIndicator } from '@vrplatform/embed/components';
import {
  ApiTokensIcon,
  BillingIcon,
  BankAccountIcon,
  DashboardIcon,
  FingerprintIcon,
  Icon,
  MembersIcon,
  NotificationIcon,
  OwnerPortalIcon,
  OwnerStatementsIcon,
  PercentageCircleIcon,
  UserIcon,
} from '@vrplatform/icons';
import { Center, Stack } from '@mantine/core';
import type { ComponentType, ReactNode } from 'react';
import { VRPilotBadge } from '../components/vrpilot-primitives';
import {
  canAccessSettingsAchPayments,
  SETTINGS_ACH_PAYMENTS_PERMISSIONS,
} from '../views/settings/SettingsAchPaymentsView.utils';

export type SettingsNavigationProps = {
  NavbarRoute: ComponentType<{
    title: string;
    link: string;
    icon: ReactNode;
  }>;
  NavigationSection: ComponentType<{
    children: ReactNode;
    title?: string;
  }>;
};

export const SettingsNavigation = ({
  NavbarRoute,
  NavigationSection,
}: SettingsNavigationProps) => {
  const [dashboard] = useDashboard();

  return (
    <Stack gap="lg">
      {dashboard === 'owner' ? (
        <Owner NavbarRoute={NavbarRoute} />
      ) : (
        <PM NavbarRoute={NavbarRoute} NavigationSection={NavigationSection} />
      )}
    </Stack>
  );
};

const PM = ({ NavbarRoute, NavigationSection }: SettingsNavigationProps) => {
  const [dashboard] = useDashboard();
  const { isTeamAdmin } = useTeamRole();
  const [team] = useTeam();
  const membership = useTeamMembership();
  const { ACH } = useEnabledFeatures();
  const uiPermissionRoles = useUiPermissionRoles();
  const { showBilling, isPartnerClient } = useBilling();
  const isPmDashboard = dashboard === 'propertyManager';
  const canAccessMembers = useSettingsPermission('teamMembers');
  const canAccessTeamGeneral = useSettingsPermission('teamGeneral');
  const canAccessTeamSecurity = useSettingsPermission('teamSecurity');
  const canAccessBooksClosed = useSettingsPermission('accountingBooksClosed');
  const canAccessAccounts = useSettingsPermission('accountingAccounts');
  const canAccessAccountingPreferences = useSettingsPermission(
    'accountingPreferences'
  );
  const canAccessTaxRates = useSettingsPermission('accountingTaxRates');
  const canAccessOpeningBalances = useSettingsPermission(
    'accountingOpeningBalances'
  );
  const canAccessVrpilotGuide = useSettingsPermission('featuresVrpilotGuide');
  const canAccessOwnerPortal = useSettingsPermission('featuresOwnerPortal');
  const canAccessEmails = useSettingsPermission('featuresEmails');
  const canAccessOwnerStatements = useSettingsPermission(
    'featuresOwnerStatements'
  );
  const canAccessApiTokens = useSettingsPermission('integrationsApiTokens');
  const { has, hasAll } = useTeamPermissions();

  const canAccessAchPayments = canAccessSettingsAchPayments({
    hasRequiredPermissions: hasAll(SETTINGS_ACH_PAYMENTS_PERMISSIONS),
    isDirectMember: membership?.accessSource === 'direct',
    isPartnerManaged: team.isPartnerManaged,
    isUiPermissionRolesEnabled: uiPermissionRoles,
    isACHPaymentsEnabled: ACH,
  });

  return (
    <>
      <NavigationSection title="User Account">
        <NavbarRoute
          title="Profile"
          link="/settings/profile"
          icon={<UserIcon size={20} />}
        />
        <NavbarRoute
          title="Security"
          link="/settings/security"
          icon={<Icon icon="ShieldCheckIcon" size={20} />}
        />
        {isPmDashboard && has('operations:read') && (
          <HiddenFeatureIndicator permission="vrp-admin">
            <NavbarRoute
              title="My refresh activity"
              link="/settings/refresh-activity"
              icon={<Icon icon="ClockIcon" size={18} />}
            />
          </HiddenFeatureIndicator>
        )}
        <HiddenFeatureIndicator permission="vrp-admin">
          <NavbarRoute
            title="Debugging"
            link="/settings/developer"
            icon={<Icon icon="GearIcon" size={18} />}
          />
        </HiddenFeatureIndicator>
      </NavigationSection>

      {dashboard !== 'overview' && (
        <>
          <Divider my={0} opacity={0.1} />
          <NavigationSection title="Team">
            {canAccessTeamGeneral && (
              <NavbarRoute
                title="General"
                link="/settings/team/general"
                icon={<DashboardIcon size={20} />}
              />
            )}
            {canAccessTeamSecurity && (
              <NavbarRoute
                title="Security"
                link="/settings/team/security"
                icon={<FingerprintIcon size={20} />}
              />
            )}

            {isPmDashboard && canAccessMembers && (
              <NavbarRoute
                title="Members"
                link="/settings/team/members"
                icon={<MembersIcon />}
              />
            )}
            {showBilling && isTeamAdmin && !isPartnerClient && (
              <NavbarRoute
                title="Billing"
                link="/settings/team/billing"
                icon={<BillingIcon />}
              />
            )}
          </NavigationSection>

          {isPmDashboard && !team.isOnboarding && (
            <NavigationSection title="Accounting">
              {canAccessBooksClosed && (
                <NavbarRoute
                  title="Books Closed Date"
                  link="/settings/accounting/books-closed"
                  icon={<Icon icon="Lock2Icon" size={18} />}
                />
              )}
              {canAccessAccounts && (
                <NavbarRoute
                  title="Accounts"
                  link="/settings/accounting/accounts"
                  icon={<Icon icon="ListUnorderedIcon" size={18} />}
                />
              )}
              {canAccessAccountingPreferences && (
                <NavbarRoute
                  title="Accounting Preferences"
                  link="/settings/accounting/reservations"
                  icon={<Icon icon="CalendarEventIcon" size={18} />}
                />
              )}
              {canAccessTaxRates && (
                <NavbarRoute
                  title="Tax Rates"
                  link="/settings/accounting/tax-rates"
                  icon={<PercentageCircleIcon size={18} />}
                />
              )}
              {canAccessOpeningBalances && (
                <NavbarRoute
                  title="Opening Balances"
                  link="/settings/accounting/opening-balances"
                  icon={<Icon icon="CircleDollarIcon" size={18} />}
                />
              )}
              {canAccessAchPayments && (
                <NavbarRoute
                  title="ACH payments"
                  link="/settings/team/ach-payments"
                  icon={
                    <Icon icon="BankAccountIcon" strokeWidth={0.6} size={18} />
                  }
                />
              )}
            </NavigationSection>
          )}

          {isPmDashboard && !team.isOnboarding && (
            <NavigationSection title="Features">
              {canAccessVrpilotGuide && (
                <NavbarRoute
                  title="VRPilot Guide"
                  link="/settings/features/vrpilot-guide"
                  icon={<VRPilotBadge size="sm" />}
                />
              )}
              {canAccessOwnerPortal && (
                <NavbarRoute
                  title="Owner Portal"
                  link="/settings/features/owner-portal"
                  icon={<OwnerPortalIcon />}
                />
              )}
              {canAccessEmails && (
                <NavbarRoute
                  title="Emails"
                  link="/settings/features/emails"
                  icon={<Icon icon="EmailIcon" size={18} />}
                />
              )}
              {canAccessOwnerStatements && (
                <NavbarRoute
                  title="Owner Statements"
                  link="/settings/features/owner-statements"
                  icon={<OwnerStatementsIcon />}
                />
              )}
            </NavigationSection>
          )}

          {canAccessApiTokens && !team.isOnboarding && (
            <NavigationSection title="Integrations">
              <NavbarRoute
                title="Api tokens"
                link="/settings/integrations/api-tokens"
                icon={<ApiTokensIcon />}
              />
            </NavigationSection>
          )}
        </>
      )}
    </>
  );
};

const Owner = ({
  NavbarRoute,
}: Pick<SettingsNavigationProps, 'NavbarRoute'>) => {
  const { hasAdminAccess } = useOwnerPortalContact();
  const { ACH } = useEnabledFeatures();
  const isUserPreview = useIsOwnerPortalPreview();
  const canAccessGeneralSettings = hasAdminAccess || isUserPreview;

  return (
    <>
      {canAccessGeneralSettings && (
        <NavbarRoute
          title="General"
          link="/settings/details"
          icon={<UserIcon size={20} />}
        />
      )}
      {ACH && canAccessGeneralSettings && (
        <NavbarRoute
          title="Payouts"
          link="/settings/payouts"
          icon={<BankAccountIcon size={20} />}
        />
      )}
      <NavbarRoute
        title="Notifications"
        link="/settings/notifications"
        icon={
          <Center w={20}>
            <NotificationIcon size={16} />
          </Center>
        }
      />
      {!isUserPreview && (
        <>
          <NavbarRoute
            title="Profile"
            link="/settings/profile"
            icon={<Icon icon="ContactAtIcon" size={20} />}
          />
          <NavbarRoute
            title="Security"
            link="/settings/security"
            icon={<Icon icon="ShieldCheckIcon" size={20} />}
          />
        </>
      )}
    </>
  );
};
